import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Users, UserPlus, Crown, Trophy, Search, Calendar, MapPin, Phone, Mail, CreditCard, Star, ToggleLeft, ToggleRight, ShoppingBag, Building2, ChevronRight } from 'lucide-react';
import { CustomerForm } from './components/CustomerForm';
import { CustomersTable } from './components/CustomersTable';
import { useCustomers } from './hooks/useCustomers';
import { Customer, CustomerFormData } from './types';

export const CustomersPage: React.FC = () => {
  const {
    filteredCustomers,
    stats,
    loading,
    error,
    filters,
    setFilters,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    reactivateCustomer,
    getCustomerDetail,
  } = useCustomers();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [deleteCustomerState, setDeleteCustomerState] = useState<Customer | null>(null);
  const [viewingCustomer, setViewingCustomer] = useState<Customer | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Función para ver detalle - obtiene stats del servidor
  const handleViewDetails = async (customer: Customer) => {
    setLoadingDetail(true);
    setViewingCustomer(customer); // Mostrar inmediatamente con datos básicos
    try {
      const fullDetail = await getCustomerDetail(customer.id);
      if (fullDetail) {
        setViewingCustomer(fullDetail); // Actualizar con datos completos (incluye stats)
      }
    } catch (err) {
      console.error('Error loading customer details:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCreateCustomer = async (data: CustomerFormData) => {
    try {
      await createCustomer(data);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  const handleUpdateCustomer = async (data: CustomerFormData) => {
    if (!editingCustomer) return;
    
    try {
      await updateCustomer(editingCustomer.id, data);
      setEditingCustomer(null);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating customer:', error);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!deleteCustomerState) return;
    
    try {
      await deleteCustomer(deleteCustomerState.id);
      setDeleteCustomerState(null);
    } catch (error) {
      console.error('Error deleting customer:', error);
    }
  };

  const openEditModal = (customer: Customer) => {
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingCustomer(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCustomer(null);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Cargando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Gestión de Clientes</h1>
        <Button 
          className="bg-macartur-primary hover:bg-macartur-accent1"
          onClick={openCreateModal}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Cliente
        </Button>
      </div>

      {/* Stats Cards - Oculto temporalmente
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Clientes</CardTitle>
            <Users className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats?.totalCustomers || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Nuevos Este Mes</CardTitle>
            <UserPlus className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.newThisMonth || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Clientes VIP</CardTitle>
            <Crown className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats?.vipCustomers || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Puntos Acumulados</CardTitle>
            <Trophy className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats?.totalLoyaltyPoints || 0}</div>
          </CardContent>
        </Card>
      </div>
      */}

      {/* Search and Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Buscar por nombre o documento..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-10"
          />
        </div>
        
        {/* Toggle para mostrar clientes inactivos */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilters({ ...filters, includeInactive: !filters.includeInactive })}
            className="flex items-center gap-2"
          >
            {filters.includeInactive ? (
              <ToggleRight className="w-4 h-4 text-green-600" />
            ) : (
              <ToggleLeft className="w-4 h-4 text-gray-400" />
            )}
            <span className="text-sm">
              {filters.includeInactive ? 'Mostrando inactivos' : 'Solo activos'}
            </span>
          </Button>
        </div>
      </div>

      {/* Customers Table */}
      <Card>
        <CardContent className="pt-6">
          <CustomersTable
            customers={filteredCustomers}
            onEdit={openEditModal}
            onDelete={(customer) => setDeleteCustomerState(customer)}
            onViewDetails={handleViewDetails}
            onReactivate={reactivateCustomer}
          />
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? 'Editar Cliente' : 'Nuevo Cliente'}
            </DialogTitle>
          </DialogHeader>
          <CustomerForm
            customer={editingCustomer ? {
              name: editingCustomer.name,
              document: editingCustomer.document,
              documentType: editingCustomer.documentType,
              email: editingCustomer.email,
              phone: editingCustomer.phone,
              address: editingCustomer.address,
              birthDate: editingCustomer.birthDate,
              tags: editingCustomer.tags,
            } : undefined}
            onSubmit={editingCustomer ? handleUpdateCustomer : handleCreateCustomer}
            onCancel={closeModal}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteCustomerState} onOpenChange={() => setDeleteCustomerState(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>¿Estás seguro de que quieres eliminar a "{deleteCustomerState?.name}"?</p>
            <p className="text-sm text-muted-foreground">
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteCustomerState(null)}>
                Cancelar
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteCustomer}
              >
                Eliminar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Details Modal */}
      <Dialog open={!!viewingCustomer} onOpenChange={() => setViewingCustomer(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-macartur-primary" />
              Detalles del Cliente
            </DialogTitle>
          </DialogHeader>
          
          {viewingCustomer && (
            <div className="space-y-6">
              {/* Header with customer name and tags */}
              <div className="border-b pb-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {viewingCustomer.name}
                </h3>
                <div className="flex gap-2">
                  {viewingCustomer.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className={
                        tag.toLowerCase() === 'vip'
                          ? 'bg-yellow-100 text-yellow-800'
                          : tag.toLowerCase() === 'frecuente'
                          ? 'bg-blue-100 text-blue-800'
                          : tag.toLowerCase() === 'inactivo'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                      }
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Customer Information Grid */}
              <div className="space-y-6">
                {/* Contact Information */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 border-b pb-2">Información de Contacto</h4>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-4 h-4 text-gray-500" />
                      <div>
                        <span className="text-sm text-gray-600">{viewingCustomer.documentType}:</span>
                        <span className="ml-2 font-medium">{viewingCustomer.document}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <div>
                        <span className="text-sm text-gray-600">Email:</span>
                        <span className="ml-2 font-medium">{viewingCustomer.email}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <div>
                        <span className="text-sm text-gray-600">Teléfono:</span>
                        <span className="ml-2 font-medium">{viewingCustomer.phone}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <div>
                        <span className="text-sm text-gray-600">Dirección:</span>
                        <span className="ml-2 font-medium">{viewingCustomer.address}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <div>
                        <span className="text-sm text-gray-600">Fecha de Nacimiento:</span>
                        <span className="ml-2 font-medium">
                          {new Date(viewingCustomer.birthDate).toLocaleDateString('es-PE')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Statistics */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 border-b pb-2">
                    Estadísticas
                    {loadingDetail && <span className="ml-2 text-sm text-gray-400">(cargando...)</span>}
                  </h4>

                  <div className="space-y-4">
                    {/* Puntos de Lealtad - Oculto temporalmente hasta implementar lógica de acumulación
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-yellow-500" />
                          <span className="text-sm font-medium">Puntos de Lealtad</span>
                        </div>
                        <span className="text-xl font-bold text-macartur-primary">
                          {viewingCustomer.loyaltyPoints}
                        </span>
                      </div>
                    </div>
                    */}

                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-green-500" />
                          <span className="text-sm font-medium">Total Gastado</span>
                        </div>
                        <span className="text-xl font-bold text-green-600">
                          {loadingDetail ? '...' : `S/ ${viewingCustomer.totalSpent?.toFixed(2) || '0.00'}`}
                        </span>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium">Visitas</span>
                        </div>
                        <span className="text-xl font-bold text-blue-600">
                          {loadingDetail ? '...' : viewingCustomer.visitCount || 0}
                        </span>
                      </div>
                    </div>
                    
                    {/* Local más visitado */}
                    {viewingCustomer.mostVisitedLocation && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-purple-500" />
                            <span className="text-sm font-medium">Local más visitado</span>
                          </div>
                          <span className="text-lg font-semibold text-purple-600">
                            {viewingCustomer.mostVisitedLocation}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Productos Favoritos */}
                    {viewingCustomer.favoriteProducts && viewingCustomer.favoriteProducts.length > 0 && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <ShoppingBag className="w-4 h-4 text-orange-500" />
                            <span className="text-sm font-medium">Productos Favoritos</span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                        
                        {/* Top 3 productos */}
                        <div className="space-y-2">
                          {viewingCustomer.favoriteProducts.slice(0, 3).map((product, index) => (
                            <div key={index} className="flex justify-between items-center text-sm">
                              <span className="text-gray-700">{product.productName}</span>
                              <span className="font-medium text-orange-600">× {product.timesOrdered}</span>
                            </div>
                          ))}
                          
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Información Adicional</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Cliente desde:</span>
                    <span className="ml-2 font-medium">
                      {new Date(viewingCustomer.createdAt).toLocaleDateString('es-PE')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Última actualización:</span>
                    <span className="ml-2 font-medium">
                      {new Date(viewingCustomer.updatedAt).toLocaleDateString('es-PE')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 border-t pt-4">
                <Button
                  variant="outline"
                  onClick={() => setViewingCustomer(null)}
                >
                  Cerrar
                </Button>
                <Button
                  onClick={() => {
                    setViewingCustomer(null);
                    openEditModal(viewingCustomer);
                  }}
                  className="bg-macartur-primary hover:bg-macartur-accent1"
                >
                  Editar Cliente
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};